import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ListShell } from "@/components/shared/ListShell";
import { ListToolbar } from "@/components/shared/ListToolbar";
import { ListPagination } from "@/components/shared/ListPagination";
import {
  DataTable,
  type DataTableColumn,
  type SelectionMode,
} from "@/components/shared/DataTable";
import { SelectionModeControl } from "@/components/shared/SelectionModeControl";
import { useUserList, type User } from "@/hooks/useUsers";
import { formatDate } from "@/utils/format";
import { downloadCsv } from "@/utils/exportCsv";

const DEFAULT_PAGE_SIZE = 50;

const exportRows = (rows: User[]) =>
  downloadCsv(
    "admin-accounts",
    ["Phone", "Email", "Role", "Status", "Created"],
    rows.map((u) => [
      u.phone,
      u.email ?? "",
      u.role,
      "active",
      formatDate(u.createdAt),
    ]),
  );

export const AdminAccountsList = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [selection, setSelection] = useState<SelectionMode>("none");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { data, isLoading, error, refetch } = useUserList({
    role: "ADMIN",
    page,
    limit,
  });
  const rows = data?.data ?? [];
  const pagination = data?.pagination;

  const setSelectionMode = (m: SelectionMode) => {
    setSelection(m);
    setSelectedIds([]);
  };

  const columns: Array<DataTableColumn<User>> = [
    {
      key: "phone",
      header: "Phone",
      render: (u) => <span className="font-medium">{u.phone}</span>,
    },
    {
      key: "email",
      header: "Email",
      render: (u) => (
        <span className="text-muted-foreground">{u.email ?? "—"}</span>
      ),
    },
    {
      key: "role",
      header: "Role",
      render: (u) => <span className="text-muted-foreground">{u.role}</span>,
    },
    {
      key: "status",
      header: "Status",
      render: () => <StatusBadge status="active" />,
    },
    {
      key: "created",
      header: "Created",
      render: (u) => (
        <span className="text-muted-foreground">{formatDate(u.createdAt)}</span>
      ),
    },
  ];

  return (
    <AppLayout title="Admin Accounts">
      <ListToolbar
        onExport={() => exportRows(rows)}
        actions={
          <>
            <SelectionModeControl
              value={selection}
              onChange={setSelectionMode}
            />
            <button
              type="button"
              onClick={() => toast.success("Create admin dialog opened")}
              className="flex items-center gap-2 bg-primary text-primary-foreground text-sm font-medium rounded-btn px-4 py-2 hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" /> Create admin
            </button>
          </>
        }
      />

      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-btn px-4 py-2 mb-3 text-sm">
          <span className="font-medium">{selectedIds.length} selected</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                toast.error(`Disabled ${selectedIds.length} admins`);
                setSelectedIds([]);
              }}
              className="px-3 py-1 rounded-btn border border-border hover:bg-muted"
            >
              Disable
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              className="px-3 py-1 rounded-btn text-muted-foreground hover:bg-muted"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <ListShell
        isLoading={isLoading}
        error={error}
        isEmpty={rows.length === 0}
        onRetry={refetch}
      >
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(u) => u.id}
          selection={selection}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
        <ListPagination
          pagination={pagination}
          page={page}
          rowsShown={rows.length}
          itemLabel="admins"
          onPageChange={setPage}
          limit={limit}
          onLimitChange={(n) => {
            setLimit(n);
            setPage(1);
          }}
        />
      </ListShell>
    </AppLayout>
  );
};

export default AdminAccountsList;
